import React, { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Card } from 'primereact/card';
import { Dropdown } from 'primereact/dropdown';
import { Button } from 'primereact/button';
import useUser from '../hooks/useUser';
import { fetchProductcategory, setSelectedClient, setSelectedBranch, resetFilters, selectClients, selectSelectedClient, selectSelectedBranch, selectLoading, selectCategoryData } from '../store/slices/productcategorySlice';
import './CategoryMaster.css';

const ProductcategoryHeader = () => {
    const dispatch = useDispatch();
    const clients = useSelector(selectClients);
    const selectedClient = useSelector(selectSelectedClient);
    const selectedBranch = useSelector(selectSelectedBranch);
    const loading = useSelector(selectLoading);
    const categoryData = useSelector(selectCategoryData);

    const { userRole, userClient } = useUser();
    const role = userRole || localStorage.getItem('role') || '';
    const roleLower = role?.toLowerCase() || '';
    const isGspos = roleLower === 'gspos';

    // Load all clients on mount
    useEffect(() => {
        dispatch(
            fetchProductcategory({
                rolename: localStorage.getItem('role') || '',
                clientname: localStorage.getItem('clientid') || ''
            })
        );
    }, [dispatch]);

    // Non-gspos roles don't get a client picker — lock onto their own client as
    // soon as the clients list (and therefore the matching client object) is available.
    useEffect(() => {
        if (isGspos || selectedClient || !clients.length) return;
        const ownClientId = userClient || localStorage.getItem('clientid') || '';
        const ownClient = clients.find((c) => String(c.client_id) === String(ownClientId));
        if (ownClient) dispatch(setSelectedClient(ownClient));
    }, [isGspos, clients, selectedClient, userClient, dispatch]);

    // When client changes, clear the selected branch
    useEffect(() => {
        dispatch(setSelectedBranch(null));
    }, [selectedClient, dispatch]);

    // Derive branches for the selected client from loaded category data
    const branches = useMemo(() => {
        if (!selectedClient?.client_id) return [];
        const seen = new Set();
        return categoryData
            .filter((item) => String(item.client_id) === String(selectedClient.client_id) && item.branch_id)
            .reduce((acc, item) => {
                if (!seen.has(String(item.branch_id))) {
                    seen.add(String(item.branch_id));
                    acc.push({ branch_id: item.branch_id, branch_name: item.branch_name || item.branch_id });
                }
                return acc;
            }, []);
    }, [categoryData, selectedClient]);

    const handleApplyFilters = () => {
        dispatch(
            fetchProductcategory({
                rolename: localStorage.getItem('role') || '',
                clientname: selectedClient?.client_id || localStorage.getItem('clientid') || '',
                branch_id: selectedBranch?.branch_id || ''
            })
        );
    };

    const handleResetFilters = () => {
        dispatch(resetFilters());
        dispatch(
            fetchProductcategory({
                rolename: localStorage.getItem('role') || '',
                clientname: localStorage.getItem('clientid') || ''
            })
        );
    };

    return (
        <>
            <div className="mb-4">
                <h2 className="text-3xl font-bold mb-2" style={{ color: 'var(--primary-color)' }}>
                    Product Category
                </h2>
                <p className="text-color-secondary">Browse and manage product categories filtered by client and branch</p>
            </div>

            <Card className="mb-4 bill-reports-filter-card">
                <div className="flex flex-wrap gap-4 align-items-end bill-reports-filter-row">
                    {/* Client — gspos only; other roles are locked to their own client */}
                    {isGspos && (
                        <div className="flex flex-column" style={{ flex: '1 1 200px', minWidth: '200px' }}>
                            <label htmlFor="client" className="mb-2 font-medium text-sm">
                                Client
                            </label>
                            <Dropdown id="client" value={selectedClient} options={clients} onChange={(e) => dispatch(setSelectedClient(e.value))} optionLabel="client_name" placeholder="Select Client" className="w-full" filter showClear />
                        </div>
                    )}

                    {/* Branch — filtered by selected client */}
                    <div className="flex flex-column" style={{ flex: '1 1 200px', minWidth: '200px' }}>
                        <label htmlFor="branch" className="mb-2 font-medium text-sm">
                            Branch
                        </label>
                        <Dropdown
                            id="branch"
                            value={selectedBranch}
                            options={branches}
                            onChange={(e) => dispatch(setSelectedBranch(e.value))}
                            optionLabel="branch_name"
                            placeholder={selectedClient ? (branches.length ? 'Select Branch' : 'No branches found') : 'Select client first'}
                            className="w-full"
                            disabled={!selectedClient || !branches.length}
                            filter
                            showClear
                        />
                    </div>

                    <div className="flex gap-2" style={{ flex: '0 0 auto' }}>
                        <Button label="Apply Filters" icon="pi pi-check" className="p-button-primary" onClick={handleApplyFilters} disabled={loading} loading={loading} />
                        <Button label="Reset" icon="pi pi-refresh" className="p-button-secondary" onClick={handleResetFilters} />
                    </div>
                </div>
            </Card>
        </>
    );
};

export default ProductcategoryHeader;
